<template>
  <div>
    <input
      id="search-place-field"
      class="basic--input"
      ref="searchPlacesField"
      type="text"
      v-model="autocompleteQuery"
      @focus="searchFieldFocused"
      @keydown.esc="cancelSearch"
      :placeholder="$t('addListing.pin.placeholder')"
    />
    <div
      ref="suggestionWrapper"
      class="wrapper--suggest"
      v-show="autocompleteSuggestions && autocompleteSuggestions.length > 0"
    >
      <place-suggestion
        v-for="(suggestion, index) in autocompleteSuggestions"
        :key="suggestion.clientKey || `${suggestion.name}-${suggestion.description}-${index}`"
        :suggestion="suggestion"
        @suggestionClicked="suggestionClicked"
        :hideSuggestion="hideSuggestion"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LodashMixin from '@/mixins/lodash';

const PlaceSuggestion = () => import('@/components/listing-form/sect1/map/place-sugestion.vue');
export default {
  name: 'search-places',
  mixins: [LodashMixin],
  components: { PlaceSuggestion },
  data() {
    return {
      hideSuggestion: false,
      sessionToken: null,
      autocompleteQuery: '',
      autocompleteSuggestions: [],
      shouldSearch: true,
      requestSequence: 0,
      locationSelected: false,
    };
  },
  created() {
    this.debounceGetSuggestion = this.debounce(this.getSuggestion, 450);
  },
  computed: {
    ...mapGetters({
      // fetchingEditListingData:'listingForm/fetchingEditListingData',
      // loadPropertyDataFromList:'listingForm/property/loadPropertyDataFromList',
    }),
    loadingMap: {
      get() {
        return this.$store.state.listingForm.sect1.location.map.loading;
      },
      set(value) {
        this.$store.commit('listingForm/sect1/location/map/SET_LOADING', value);
      },
    },
  },
  watch: {
    autocompleteQuery(value) {
      if (!this.shouldSearch) {
        this.shouldSearch = true;
        return;
      }

      this.locationSelected = false;
      this.invalidateAutocompleteRequests();

      if (value.trim().length < 3) {
        this.autocompleteSuggestions = [];
        this.endSearchSession();
        return;
      }

      this.hideSuggestion = false;
      this.autocompleteSuggestions = [];
      this.debounceGetSuggestion();
    },
  },
  mounted() {
    window.addEventListener('click', this.windowClickListener);
    if (this.autocompleteQuery.trim().length >= 3) this.getSuggestion();
  },
  beforeDestroy() {
    window.removeEventListener('click', this.windowClickListener);
    this.invalidateAutocompleteRequests();
    this.endSearchSession();
  },
  methods: {
    windowClickListener(e) {
      const clickedInput = this.$refs.searchPlacesField.contains(e.target);
      const suggestionWrapper = this.$refs.suggestionWrapper;
      const clickedSuggestion = suggestionWrapper && suggestionWrapper.contains(e.target);

      this.hideSuggestion = !clickedInput && !clickedSuggestion;
      if (this.hideSuggestion) {
        this.cancelSearch();
      }
    },
    searchFieldFocused() {
      this.hideSuggestion = false;
      if (
        !this.locationSelected &&
        this.autocompleteQuery.trim().length >= 3 &&
        this.autocompleteSuggestions.length === 0
      ) {
        this.debounceGetSuggestion();
      }
    },
    createSessionToken() {
      const cryptoApi = window.crypto;
      if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
        return cryptoApi.randomUUID();
      }

      if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        cryptoApi.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
          16,
          20,
        )}-${hex.slice(20)}`;
      }

      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
      });
    },
    getSessionToken() {
      if (!this.sessionToken) this.sessionToken = this.createSessionToken();
      return this.sessionToken;
    },
    invalidateAutocompleteRequests() {
      this.requestSequence += 1;
      this.debounceGetSuggestion.cancel();
    },
    endSearchSession() {
      this.sessionToken = null;
    },
    cancelSearch() {
      this.invalidateAutocompleteRequests();
      this.autocompleteSuggestions = [];
      this.endSearchSession();
    },
    async suggestionClicked(suggestion) {
      this.debounceGetSuggestion.cancel();
      this.autocompleteSuggestions = [];
      this.shouldSearch = false;
      this.autocompleteQuery = suggestion.description;
      const requestId = ++this.requestSequence;
      this.loadingMap = true;

      try {
        let latitude = suggestion.latitude;
        let longitude = suggestion.longitude;

        if (latitude == null || longitude == null) {
          if (!suggestion.resolve_token) {
            throw new Error('Location coordinates are missing and cannot be resolved.');
          }

          const response = await this.$http.post('/api/v2/map/resolve_place', {
            resolve_token: suggestion.resolve_token,
          });
          const resolvedLocation =
            response.data && response.data.data !== undefined ? response.data.data : response.data;
          latitude = resolvedLocation && resolvedLocation.latitude;
          longitude = resolvedLocation && resolvedLocation.longitude;
        }

        if (requestId !== this.requestSequence) return;
        if (latitude == null || longitude == null) {
          throw new Error('The location service did not return coordinates.');
        }

        this.$store.commit('listingForm/sect1/location/map/SET_LAT', latitude);
        this.$store.commit('listingForm/sect1/location/map/SET_LNG', longitude);
        this.locationSelected = true;
        this.hideSuggestion = true;
        this.endSearchSession();
        this.$emit('placeDetailFound');
      } catch (error) {
        if (requestId === this.requestSequence) {
          this.autocompleteSuggestions = [suggestion];
          this.hideSuggestion = false;
          if (!error.response) console.error(error);
        }
      } finally {
        this.loadingMap = false;
      }
    },
    async getSuggestion() {
      const userInput = this.autocompleteQuery.trim();
      if (userInput.length < 3 || this.hideSuggestion) {
        this.autocompleteSuggestions = [];
        return;
      }

      const requestId = ++this.requestSequence;
      try {
        const response = await this.$http.post('/api/v2/map/place_autocomplete', {
          user_input: userInput,
          session_token: this.getSessionToken(),
        });
        if (requestId !== this.requestSequence || userInput !== this.autocompleteQuery.trim()) {
          return;
        }

        const suggestions = response.data && response.data.data;
        this.autocompleteSuggestions = Array.isArray(suggestions)
          ? suggestions.map((suggestion, index) => ({
              ...suggestion,
              clientKey: `${requestId}-${index}`,
            }))
          : [];
      } catch (error) {
        if (requestId === this.requestSequence) {
          this.autocompleteSuggestions = [];
          if (!error.response) console.error(error);
        }
      }
    },
  },
};
</script>

<style scoped></style>
